import cardValidator from 'card-validator';
import { CardBrand, PaymentCard } from 'src/store/subscriptionStore';

export interface DetectedCardInfo {
  brand: CardBrand;
  niceType: string;
  isAmex: boolean;
  gaps: number[];
  maxLength: number;
  codeName: 'CID' | 'CVC';
  codeSize: number;
  isValid: boolean;
  isPotentiallyValid: boolean;
  card: ReturnType<typeof cardValidator.number>['card'];
}

/**
 * Normalizes card-validator card type string into the application's CardBrand union.
 */
export const normalizeCardBrand = (type?: string | null): CardBrand => {
  if (!type) return 'generic';
  const norm = type.toLowerCase().replace(/[\s-_]/g, '');
  if (norm.includes('visa')) return 'visa';
  if (norm.includes('master')) return 'mastercard';
  if (norm.includes('americanexpress') || norm.includes('amex')) return 'amex';
  if (norm.includes('discover')) return 'discover';
  if (norm.includes('jcb')) return 'jcb';
  if (norm.includes('diner')) return 'diners';
  if (norm.includes('union')) return 'unionpay';
  if (norm.includes('maestro')) return 'maestro';
  return 'generic';
};

/**
 * Returns human-friendly display name for a card brand.
 */
export const getCardBrandDisplayName = (brand?: string | null): string => {
  const norm = (brand || '').toLowerCase().replace(/[\s-_]/g, '');
  if (norm.includes('visa')) return 'Visa';
  if (norm.includes('master')) return 'Mastercard';
  if (norm.includes('amex') || norm.includes('americanexpress')) return 'American Express';
  if (norm.includes('discover')) return 'Discover';
  if (norm.includes('jcb')) return 'JCB';
  if (norm.includes('diner')) return 'Diners Club';
  if (norm.includes('union')) return 'UnionPay';
  if (norm.includes('maestro')) return 'Maestro';
  return 'Card';
};

/**
 * Formats a continuous digit string with space separators at specific gaps.
 * E.g., for standard 16 digits with gaps [4, 8, 12] -> '4111 1111 1111 1111'
 * For Amex 15 digits with gaps [4, 10] -> '3782 822463 10005'
 * For Diners Club 14 digits with gaps [4, 10] -> '3612 345678 9012'
 */
export const formatCardNumberWithGaps = (
  digits: string,
  gaps: number[] = [4, 8, 12],
  maxLength = 19,
): string => {
  const capped = digits.substring(0, maxLength);
  const parts: string[] = [];
  let prev = 0;
  for (const gap of gaps) {
    if (capped.length > gap) {
      parts.push(capped.substring(prev, gap));
      prev = gap;
    } else {
      break;
    }
  }
  parts.push(capped.substring(prev));
  return parts.filter(Boolean).join(' ');
};

/**
 * Formats expiration input MM / YY.
 */
export const formatExpiry = (value: string): string => {
  const digits = value.replace(/\D/g, '').substring(0, 4);
  if (digits.length >= 3) {
    return `${digits.substring(0, 2)} / ${digits.substring(2)}`;
  }
  return digits;
};

/**
 * Formats masked card number with proper brand prefix mask.
 * E.g., for Amex: "•••• •••••• •" + last4
 * For other cards: "•••• •••• •••• " + last4
 */
export const formatMaskedCardNumber = (brand: string | undefined | null, last4: string): string => {
  const norm = (brand || '').toLowerCase().replace(/[\s-_]/g, '');
  const isAmex = norm.includes('amex') || norm.includes('americanexpress');
  return `${isAmex ? '•••• •••••• •' : '•••• •••• •••• '}${last4}`;
};

/**
 * Parses card number and identifies card brand, gaps, length, CVC requirements, and validity.
 */
export const getCardInfo = (
  rawNumber: string,
  cardToEdit?: PaymentCard | null,
): DetectedCardInfo => {
  const cleanNumber = rawNumber.replace(/\D/g, '');

  if (cardToEdit && !cleanNumber) {
    const isAmex = cardToEdit.brand === 'amex';
    return {
      brand: cardToEdit.brand,
      niceType: getCardBrandDisplayName(cardToEdit.brand),
      isAmex,
      gaps: isAmex ? [4, 10] : [4, 8, 12],
      maxLength: isAmex ? 15 : 16,
      codeName: isAmex ? 'CID' : 'CVC',
      codeSize: isAmex ? 4 : 3,
      isValid: true,
      isPotentiallyValid: true,
      card: null,
    };
  }

  const validation = cardValidator.number(cleanNumber);
  let card = validation.card;

  // Single-digit and early 2-digit high-confidence heuristics before card-validator's full match
  if (!card && cleanNumber.length > 0) {
    if (cleanNumber.startsWith('4')) {
      card = {
        type: 'visa',
        niceType: 'Visa',
        patterns: [4],
        gaps: [4, 8, 12],
        lengths: [16, 18, 19],
        code: { name: 'CVV', size: 3 },
      };
    }
  }

  const detectedBrand = card ? normalizeCardBrand(card.type) : 'generic';
  const isAmex =
    detectedBrand === 'amex' || cleanNumber.startsWith('34') || cleanNumber.startsWith('37');

  const gaps = card?.gaps || (isAmex ? [4, 10] : [4, 8, 12]);
  const maxLength = card?.lengths ? Math.max(...card.lengths) : isAmex ? 15 : 19;
  const codeName: 'CID' | 'CVC' = isAmex ? 'CID' : card?.code?.name === 'CID' ? 'CID' : 'CVC';
  const codeSize = isAmex ? 4 : 3;

  return {
    card,
    brand: detectedBrand,
    niceType: card?.niceType || (isAmex ? 'American Express' : 'Card'),
    isAmex,
    gaps,
    maxLength,
    codeName,
    codeSize,
    isValid: validation.isValid,
    isPotentiallyValid: validation.isPotentiallyValid,
  };
};

/**
 * Validates a card number using card-validator and brand specific constraints.
 */
export const validateCardNumber = (rawNumber: string, isAmex?: boolean, isEdit = false): string => {
  const clean = rawNumber.replace(/\D/g, '');
  if (!clean && !isEdit) {
    return 'Card number is required';
  }
  if (!isEdit && clean) {
    const v = cardValidator.number(clean);
    if (clean.length < 13 || (!v.isValid && !v.isPotentiallyValid)) {
      return 'Please enter a valid card number';
    }
    if (isAmex && clean.length !== 15) {
      return 'American Express card number must be 15 digits';
    }
    if (!v.isValid && clean.length >= 15) {
      return isAmex
        ? 'Enter a valid 15-digit American Express card number'
        : 'Please enter a valid card number';
    }
  }
  return '';
};

/**
 * Validates card expiration date using card-validator.
 */
export const validateExpiry = (expiry: string): string => {
  const trimmed = expiry.trim();
  if (!trimmed) {
    return 'Expiry date is required';
  }
  const expVal = cardValidator.expirationDate(trimmed);
  if (!expVal.isValid) {
    return 'Enter a valid expiration date (MM / YY)';
  }
  return '';
};

/**
 * Validates CVC / CID code based on brand length requirements.
 */
export const validateCvc = (
  rawCvc: string,
  codeSize: number,
  codeName = 'CVC',
  isEdit = false,
): string => {
  const clean = rawCvc.replace(/\D/g, '');
  if (!clean && !isEdit) {
    return `${codeName} is required`;
  }
  if (!isEdit && clean) {
    if (clean.length !== codeSize) {
      return `${codeName} must be ${codeSize} digits`;
    }
    const cvvVal = cardValidator.cvv(clean, codeSize);
    if (!cvvVal.isValid) {
      return `Invalid ${codeName}`;
    }
  }
  return '';
};

/**
 * Validates cardholder name.
 */
export const validateCardholderName = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) {
    return 'Cardholder name is required';
  }
  if (!/\p{L}/u.test(trimmed)) {
    return 'Name must contain letters';
  }
  return '';
};
